import { action, computed, observable, makeObservable } from "mobx";
import { MrbBaseEditViewStore } from "mrb/core";
import { MrbFieldSelectStore } from "mrb/components/select";
import { MrbSelectStore } from "mrb/components/select/stores";
import { NakiNigunAlbumEditForm } from "administration/naki-nigun/forms";
import { NakiNigunAlbumCoverImageUploadViewStore } from "administration/naki-nigun/stores";
import { isNil, map, sumBy, some, uniqBy } from "lodash";

// The selectable release-date range, and with it the year dropdown's contents.
//
// react-datepicker builds its year list once, as `selectedYear - n .. selectedYear + n` clamped to
// these two bounds, and hides its "load more years" arrows as soon as the list reaches a bound. So a
// wide yearDropdownItemNumber plus these bounds yields the whole range in one scrollable list with no
// paging arrows at all. Without them the arrows shift the list by a single year per click, which put
// a 1990 album 36 clicks away.
//
// Evaluated once at module load: a stable identity keeps react-datepicker from seeing a new prop on
// every render. The upper bound runs to the end of next year so an announced-but-unreleased album can
// still be dated ahead, and the list opens on the newest years, which is where nearly every edit is.
const RELEASE_DATE_MIN = new Date(1900, 0, 1);
const RELEASE_DATE_MAX = new Date(new Date().getFullYear() + 1, 11, 31);

// Normalizes MrbDatePicker's selection into the `YYYY-MM-DDT00:00:00` string the API stores.
//
// The picker hands back a UTC instant (`value.toISOString()` inside MrbDatePicker) built from a
// local Date, so the calendar date has to be read back with local getters. Slicing the ISO string
// instead would read the UTC calendar date, which is a day off for anyone not at UTC+0: local
// midnight on 14 March serialises as `...03-13T23:00:00Z` for anyone ahead of UTC. Rebuilding the
// string by hand rather than passing the ISO value straight through keeps the stored value a plain
// calendar date with no offset, matching the column's `timestamp without time zone` type.
function normalizeReleaseDate(isoValue) {
    if (isNil(isoValue)) {
        return null;
    }
    const date = new Date(isoValue);
    if (isNaN(date.getTime())) {
        return null;
    }
    const pad = (value) => `${value}`.padStart(2, "0");
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T00:00:00`;
}

class ProducerRow {
    @observable producerId = null;
    @observable revenuePercentage = 0;
    @observable isActive = true;
    producerSelectStore = null;

    constructor(findProducers, existingProducer = null) {
        makeObservable(this);

        this.producerSelectStore = new MrbSelectStore({
            isSearchable: true,
            virtualized: false,
            initFetch: false,
            textKey: "name",
            valueKey: "id",
            isClearable: true,
            actions: {
                find: (searchTerm) => findProducers(searchTerm),
                onChange: action((selectedOption) => {
                    this.producerId = selectedOption ? selectedOption.value : null;
                }),
            },
        });

        if (existingProducer) {
            this.producerId = existingProducer.producerId;
            this.revenuePercentage = existingProducer.revenuePercentage;
            this.isActive = existingProducer.isActive;
            if (existingProducer.producer) {
                this.producerSelectStore.setSelectedItem(existingProducer.producer);
            }
        }
    }
}

class NakiNigunAddAlbumViewStore extends MrbBaseEditViewStore {
    @observable producerRows = [];

    @computed get totalRevenue() {
        return sumBy(this.producerRows, (row) => row.revenuePercentage);
    }

    @computed get producersAreValid() {
        if (this.producerRows.length === 0) {
            return true;
        }
        if (some(this.producerRows, (row) => isNil(row.producerId))) {
            return false;
        }
        if (some(this.producerRows, (row) => row.revenuePercentage < 0.01)) {
            return false;
        }
        const uniqueProducers = uniqBy(this.producerRows, (row) => row.producerId);
        if (uniqueProducers.length !== this.producerRows.length) {
            return false;
        }
        return Math.round(this.totalRevenue * 100) / 100 === 100;
    }

    @computed get episodeSortTypeOptions() {
        return this.nakiNigunArtistEditViewStore.episodeSortTypeOptions;
    }

    @computed get isPremiumDisabled() {
        return this.nakiNigunArtistEditViewStore.form.$("isPremium").value;
    }

    // Locked while the parent artist is female-artist content: only the Kedayo Pink may carry it,
    // so the radio / app / Naki Bolt defaults are not meaningful. The Pink default stays editable.
    @computed get isFemaleContent() {
        return this.nakiNigunArtistEditViewStore.form.$("isFemale").value === true;
    }

    @computed get isActiveStatusDisabled() {
        return !this.nakiNigunArtistEditViewStore.form.$("isActive").value;
    }

    @computed get showScheduledRelease() {
        return !this.form.$("isActive").value;
    }

    // The picker reads the field's own stored string. `moment(value).toDate()` inside MrbDatePicker
    // parses an offset-less `YYYY-MM-DDT00:00:00` as local time, so the calendar shows the same
    // calendar date that is stored, and normalizeReleaseDate converts the selection back the same
    // way. The round-trip cannot drift because both directions go through local time.
    @computed get releaseDatePickerValue() {
        return this.form.$("releaseDate").value || null;
    }

    get releaseDateMinDate() {
        return RELEASE_DATE_MIN;
    }

    get releaseDateMaxDate() {
        return RELEASE_DATE_MAX;
    }

    constructor(rootStore, { nakiNigunArtistEditViewStore }) {
        super(rootStore, {
            name: "naki-nigun-album",
            FormClass: NakiNigunAlbumEditForm,
            redirectOnCreateSuccess: false,
        });
        this.nakiNigunArtistEditViewStore = nakiNigunArtistEditViewStore;
        this.routeStore = this.nakiNigunArtistEditViewStore.routeStore;
        makeObservable(this);

        this.coverImageUploadViewStore = new NakiNigunAlbumCoverImageUploadViewStore(this.rootStore, {
            routeStore: this.nakiNigunArtistEditViewStore.routeStore,
        });

        this.reaction(
            () => this.nakiNigunArtistEditViewStore.form.$("isPremium").value,
            (isPremium) => {
                this.form.$("isPremium").set(isPremium);
            }
        );

        this.reaction(
            () => this.nakiNigunArtistEditViewStore.form.$("isActive").value,
            (isActive) => {
                this.form.$("isActive").set(isActive);
            }
        );

        this.genreSelectStore = new MrbFieldSelectStore(
            {
                isSearchable: true,
                virtualized: false,
                isMulti: true,
                actions: {
                    find: (searchTerm) => {
                        return this.routeStore.findGenres(searchTerm);
                    },
                    createOption: (name) => {
                        return this.routeStore.createGenre({ name });
                    },
                },
            },
            this.form.$("genreIds")
        );

        this.findProducers = this.routeStore.findProducers.bind(this.routeStore);
    }

    @action.bound
    setReleaseDate(isoValue) {
        this.form.$("releaseDate").set("value", normalizeReleaseDate(isoValue) || "");
    }

    // Selecting a year from the calendar's year dropdown dates the album to 1 January of that year,
    // which is the same "year only" convention the retired Year column was backfilled with. The
    // string is built directly rather than through a Date, so there is no local/UTC conversion to
    // drift.
    //
    // Only the year dropdown reaches this: react-datepicker's decreaseMonth/increaseMonth call
    // handleMonthChange alone, never handleYearChange, so stepping the calendar across a year
    // boundary does not fire onYearChange and cannot reset the date behind the user's back.
    @action.bound
    setReleaseYear(dateOrYear) {
        const year = dateOrYear instanceof Date ? dateOrYear.getFullYear() : parseInt(dateOrYear, 10);
        if (isNaN(year)) {
            return;
        }
        this.form.$("releaseDate").set("value", `${year}-01-01T00:00:00`);
    }

    create(data) {
        if (!this.producersAreValid) {
            return;
        }
        data.coverImageId = this.coverImageUploadViewStore.uploadedFileId;
        const selectedGenres = this.genreSelectStore.selectedOption;
        if (selectedGenres) {
            data.genres = map(selectedGenres, (genreOption) => genreOption.item);
        }
        data.producers = map(this.producerRows, (row) => ({
            isActive: row.isActive,
            producerId: row.producerId,
            producer: row.producerSelectStore.selectedItem,
            revenuePercentage: row.revenuePercentage,
        }));
        data.releaseDate = data.releaseDate || null;
        const scheduledReleaseAt = this.form.$("scheduledReleaseAt").value || null;
        data.scheduledReleaseAt = data.isActive
            ? null
            : scheduledReleaseAt
            ? new Date(scheduledReleaseAt).toISOString()
            : null;
        this.nakiNigunArtistEditViewStore.addAlbum(data);
        return Promise.resolve();
    }

    onCreateError(err) {
        this.rootStore.notificationStore.error("An unexpected error occurred while trying to add album.", err);
    }

    @action.bound
    addProducerRow() {
        this.producerRows.push(new ProducerRow(this.findProducers));
    }

    @action.bound
    removeProducerRow(index) {
        this.producerRows.splice(index, 1);
    }

    @action.bound
    setProducerRowRevenue(index, value) {
        const parsed = parseFloat(value);
        this.producerRows[index].revenuePercentage = isNaN(parsed)
            ? 0
            : Math.round(Math.max(0, Math.min(100, parsed)) * 100) / 100;
    }

    resetForm() {
        this.form.clear();
        this.coverImageUploadViewStore.remove();
        const isArtistPremium = this.nakiNigunArtistEditViewStore.form.$("isPremium").value;
        this.form.set({
            isActive: false,
            isTurnedOnByDefault: true,
            isTurnedOnByDefaultInApp: true,
            isTurnedOnByDefaultInEmbeddedApp: true,
            isTurnedOnByDefaultInHandheldPinkApp: true,
            isTurnedOnByDefaultInHandheldApp: true,
            albumFeed: {
                contentSortTypeId: this.episodeSortTypeOptions[0].value,
                recentlyUpdatedVisibility: true,
            },
            isPremium: isArtistPremium,
        });
        this.genreSelectStore.setSelectedItem(null);
        this.producerRows = [];
    }

    onCreateSuccess() {
        this.resetForm();
    }
}

export default NakiNigunAddAlbumViewStore;
